import * as React from 'react';
import {Download, Paperclip, SquareX} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {toCssSize} from '../../lib/utils';
import {useCombinedRef} from '../../hooks';
import {FieldErrorMessage} from '../../field-error-message';
import {Fieldset} from '../_fieldset';

type FileInputValue = File | string | null;

type FileInputProps = {
  label?: string;
  value?: FileInputValue;
  onChange?: (value: File | null, event?: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onDownload?: (url: string) => void;
  name?: string;
  placeholder?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  invalid?: boolean;
  error?: string;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  helperText?: string;
  hideErrorMessage?: boolean;
  className?: string;
  width?: number | string;
  downloadLabel?: string;
  fileNameFromUrl?: (url: string) => string;
};

function defaultDownload(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(function FileInput(
  {
    label,
    value,
    onChange,
    onFocus,
    onDownload = defaultDownload,
    name = 'file',
    placeholder,
    accept,
    multiple,
    disabled,
    readOnly,
    loading,
    invalid,
    error,
    optional,
    tooltip,
    helperText,
    hideErrorMessage,
    className,
    width,
    downloadLabel,
  },
  ref,
) {
  const internalRef = React.useRef<HTMLInputElement>(null);
  const inputRef = useCombinedRef(ref, internalRef);
  const {t} = useTranslation();
  const resolvedLabel = label ?? t('upload_file');
  const resolvedDownloadLabel = downloadLabel ?? t('download_attachment');
  const reactId = React.useId();
  const inputId = `${name || 'dash-file'}-${reactId}`;
  const labelId = `${inputId}-label`;
  const errorId = `${inputId}-error`;

  const isBlocked = Boolean(disabled) || Boolean(loading);
  const isInvalid = Boolean(invalid || error);
  const isEmpty = !value;
  const wrapperWidth = toCssSize(width);
  const isUrl = typeof value === 'string' && value.length > 0;
  const isFile = typeof File !== 'undefined' && value instanceof File;
  const hasFileChip = isUrl || isFile;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    onChange?.(file, event);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (internalRef.current) internalRef.current.value = '';
    onChange?.(null);
  };

  const handleDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isUrl) onDownload(value as string);
  };

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'relative block w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)] cursor-pointer text-left',
        (disabled || readOnly) && 'cursor-not-allowed',
        loading && 'cursor-progress opacity-50',
        disabled && 'opacity-50',
        className,
      )}
      style={wrapperWidth ? {width: wrapperWidth} : undefined}
    >
      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={isBlocked || readOnly}
        onChange={handleFileChange}
        onFocus={onFocus}
        className="absolute h-[0.1px] w-[0.1px] opacity-0"
        aria-labelledby={labelId}
        aria-invalid={isInvalid}
      />
      <div className="relative w-full">
        <div className="relative w-full">
          <div
            className={cn(
              'relative box-border flex h-12 w-full items-center justify-between gap-2 rounded-[6px] px-4 text-[16px] font-medium text-[var(--chekin-color-brand-navy)]',
              isEmpty && 'bg-[var(--empty-field-background)]',
            )}
          >
            {hasFileChip ? (
              <div
                className="inline-flex h-6 max-w-[85%] items-center rounded-[4px] border border-[var(--chekin-neutral-400)] bg-[var(--chekin-neutral-75)] pl-[10px] pr-1"
                onClick={event => event.preventDefault()}
              >
                {isUrl ? (
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex items-center gap-[7px] truncate border-0 bg-transparent p-0 text-[14px] font-medium text-[var(--chekin-color-brand-navy)] outline-none"
                  >
                    <span className="truncate">{resolvedDownloadLabel}</span>
                    <Download size={15} />
                  </button>
                ) : (
                  <span className="truncate text-[14px] font-medium text-[var(--chekin-color-brand-navy)]">
                    {(value as File).name}
                  </span>
                )}
                <button
                  type="button"
                  disabled={isBlocked || readOnly}
                  onClick={handleClear}
                  className="ml-2 flex h-[15px] w-[15px] items-center justify-center rounded-[3px] border-0 bg-transparent p-0 text-[var(--chekin-neutral-500)] outline-none hover:shadow-[0_3px_3px_#0f477734]"
                  aria-label={t('remove_file')}
                >
                  <SquareX
                    size={15}
                    fill="var(--chekin-neutral-500)"
                    color="#f8f8f8"
                    strokeWidth={1.8}
                  />
                </button>
              </div>
            ) : (
              <span className="block min-w-0 flex-1 truncate text-left text-[var(--chekin-color-gray-1)]">
                {placeholder ?? ''}
              </span>
            )}
            <span className="ml-auto flex items-center gap-2 text-[var(--chekin-color-gray-2)]">
              <Paperclip size={20} />
            </span>
          </div>
          <Fieldset
            isFocused={false}
            invalid={isInvalid}
            isEmpty={isEmpty}
            isActivated={!isEmpty}
            disabled={disabled}
            loading={loading}
            readOnly={readOnly}
            htmlFor={inputId}
            labelId={labelId}
            legend={resolvedLabel}
            label={resolvedLabel}
            tooltip={tooltip}
          />
        </div>
        {!error && optional && (
          <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
            {typeof optional === 'string' ? optional : t('optional')}
          </span>
        )}
        {!error && helperText && (
          <span className="mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
            {helperText}
          </span>
        )}
        {error && !hideErrorMessage && (
          <FieldErrorMessage
            id={errorId}
            message={error}
            className="mt-[1px] text-[14px]"
          />
        )}
      </div>
    </label>
  );
});

export {FileInput};
export type {FileInputProps, FileInputValue};
